import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const socials = [
  { key: "instagram", Icon: Instagram },
  { key: "facebook", Icon: Facebook },
  { key: "linkedin", Icon: Linkedin },
  { key: "youtube", Icon: Youtube },
];

export const Footer = () => {
  const { pathname } = useLocation();
  const resolve = (href) => (pathname !== "/" ? `/${href}` : href);
  const site = useSiteSettings();
  const activeSocials = socials.filter(({ key }) => site[key]);

  return (
    <footer className="bg-ink" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-14 md:px-10 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="inline-block rounded-lg bg-white px-4 py-3" data-testid="footer-logo">
              <img src="/assets/veera-horizontal.png" alt="Urban Veera — Income-Producing Real Estate" className="h-11 w-auto" />
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/50">
              Property. Ownership. Opportunity. Urban Veera builds premium
              property models around land and building ownership — structured
              for long-term rental potential.
            </p>
            {activeSocials.length > 0 && (
              <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                {activeSocials.map(({ key, Icon }) => (
                  <a
                    key={key}
                    href={site[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Urban Veera on ${key}`}
                    data-testid={`footer-social-${key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/50 transition-colors duration-300 hover:border-amber hover:text-amber"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <p className="font-spec text-[10px] uppercase tracking-[0.25em] text-white/40">Markets</p>
            <ul className="mt-5 space-y-2.5 text-sm text-white/70">
              <li className="font-bold text-white">Bengaluru — home market</li>
              {["Mangalore", "Pune", "Mumbai", "Ranchi", "Bihar"].map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-spec text-[10px] uppercase tracking-[0.25em] text-white/40">Explore</p>
            <ul className="mt-5 space-y-2.5 text-sm text-white/70">
              <li><a href={resolve("#model")} className="transition-colors duration-300 hover:text-amber" data-testid="footer-link-model">The Model</a></li>
              <li><a href={resolve("#projects")} className="transition-colors duration-300 hover:text-amber" data-testid="footer-link-projects">Projects</a></li>
              <li><a href={resolve("#segments")} className="transition-colors duration-300 hover:text-amber" data-testid="footer-link-segments">Segments</a></li>
              <li><a href={resolve("#why")} className="transition-colors duration-300 hover:text-amber" data-testid="footer-link-why">Why Urban Veera</a></li>
              <li><Link to="/properties" className="transition-colors duration-300 hover:text-amber" data-testid="footer-link-properties">Properties</Link></li>
              <li><Link to="/admin" className="text-white/40 transition-colors duration-300 hover:text-amber" data-testid="admin-login-link">Admin</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-spec text-[10px] uppercase tracking-[0.25em] text-white/40">Talk to us</p>
            <a
              href={`tel:${site.phone.replace(/\s/g, "")}`}
              data-testid="footer-phone-link"
              className="mt-5 flex items-center gap-2 text-sm font-bold text-amber transition-colors duration-300 hover:text-white"
            >
              <Phone size={14} />
              {site.phone}
            </a>
            <a
              href={`https://wa.me/${site.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="mt-3 inline-block text-sm text-white/70 transition-colors duration-300 hover:text-amber"
            >
              WhatsApp us
            </a>
            <a
              href={`mailto:${site.email}`}
              data-testid="footer-email-link"
              className="mt-3 flex items-center gap-2 text-sm text-white/70 transition-colors duration-300 hover:text-amber"
            >
              <Mail size={13} />
              {site.email}
            </a>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-7">
          <p className="max-w-4xl font-spec text-[10px] leading-relaxed tracking-wide text-white/35" data-testid="footer-disclaimer">
            Urban Veera provides project coordination, feasibility and real-estate development services. Illustrative project figures are estimates and are not guarantees of rental income, appreciation, occupancy or investment returns. Actual outcomes depend on site conditions, approvals, design, construction costs, market conditions, tenant demand, operating expenses and other project-specific factors. Development potential shown in examples is illustrative and subject to the specific site's applicable laws, regulations and sanctioned plans.
          </p>
        </div>

        <div className="mt-7 flex flex-col gap-3 border-t border-white/10 pt-7 md:flex-row md:items-center md:justify-between">
          <p className="font-spec text-[10px] uppercase tracking-[0.18em] text-white/40" data-testid="footer-copyright">
            © 2026 Urban Veera · Bengaluru
          </p>
          <p className="font-spec text-[10px] uppercase tracking-[0.18em] text-white/40">Property · Ownership · Opportunity</p>
        </div>
      </div>
    </footer>
  );
};
